import { getRetailerPriceModule } from '../comparisons/retailerPriceModule';
import {
  getCanonicalPdpOffers,
  getPriceFirstPdpCopy,
  isPriceFirstPdpSlug,
} from './pdpPricePageView';

export const PDP_SEO_ATTR = 'data-ab-pdp-seo';
export const PDP_JSON_LD_ID = 'ab-pdp-product-jsonld';
export const PDP_PUBLIC_ORIGIN = 'https://www.anglerbuilt.com';

export function resolvePdpPublicOrigin(origin) {
  const raw =
    origin ||
    (typeof window !== 'undefined' && window.location && window.location.origin) ||
    PDP_PUBLIC_ORIGIN;
  return String(raw).replace(/\/$/, '');
}

export function toAbsolutePdpImageUrl(imageUrl, origin) {
  if (!imageUrl || typeof imageUrl !== 'string') return '';
  const trimmed = imageUrl.trim();
  if (!trimmed) return '';
  if (/^(blob:|data:)/i.test(trimmed)) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return `${resolvePdpPublicOrigin(origin)}${path}`;
}

export function buildPdpCanonicalUrl(slug, origin) {
  const cleanSlug = String(slug || '').trim();
  if (!cleanSlug) return '';
  return `${resolvePdpPublicOrigin(origin)}/simple-products/${cleanSlug}`;
}

function currentEligiblePrices(retailerModule) {
  return [...(retailerModule?.primaryRetailers || []), ...(retailerModule?.otherCurrentRetailers || [])]
    .filter(
      (row) =>
        row &&
        row.isCurrent &&
        typeof row.price === 'number' &&
        Number.isFinite(row.price) &&
        row.price > 0
    )
    .map((row) => row.price);
}

export function buildPdpAggregateOffer(retailerModule) {
  const prices = currentEligiblePrices(retailerModule);
  if (!prices.length) return null;
  return {
    '@type': 'AggregateOffer',
    priceCurrency: 'USD',
    lowPrice: Math.min(...prices),
    highPrice: Math.max(...prices),
    offerCount: prices.length,
  };
}

export function buildPdpSeoData({
  productSlug,
  productImage,
  brand,
  mpn,
  retailerModule,
  prefetchedOffers,
  origin,
  now,
} = {}) {
  const slug = String(productSlug || '').trim();
  if (!isPriceFirstPdpSlug(slug)) return null;

  const copy = getPriceFirstPdpCopy(slug);
  if (!copy?.displayTitle) return null;

  const publicOrigin = resolvePdpPublicOrigin(origin);
  const name = String(copy.schemaName || copy.displayTitle).trim();
  const description = String(copy.orientation || '').trim();
  const image = toAbsolutePdpImageUrl(productImage, publicOrigin);
  const module =
    retailerModule || getRetailerPriceModule(getCanonicalPdpOffers(prefetchedOffers), { now });
  // Price-first LiveScope 2 PDPs are Garmin products. Prefer the DB brand when
  // it is Garmin; do not infer another manufacturer.
  const brandName = String(brand || '').trim() === 'Garmin' ? 'Garmin' : 'Garmin';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
  };
  if (description) jsonLd.description = description;
  if (image) jsonLd.image = image;
  jsonLd.brand = {
    '@type': 'Brand',
    name: brandName,
  };
  const trimmedMpn = String(mpn || '').trim();
  if (trimmedMpn) jsonLd.mpn = trimmedMpn;

  const offers = buildPdpAggregateOffer(module);
  if (offers) jsonLd.offers = offers;

  const configuredDescription = String(copy.metaDescription || '').trim();
  const metaDescription =
    configuredDescription ||
    `Compare current verified retailer prices for ${name}, see when offers were checked, and find comparison and compatibility help from Angler Built.`;

  const configuredTitle = String(copy.documentTitle || '').trim();

  return {
    slug,
    title: configuredTitle || `${copy.displayTitle} Price & Retailer Offers | Angler Built`,
    metaDescription,
    canonical: buildPdpCanonicalUrl(slug, publicOrigin),
    jsonLd,
  };
}
